import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import { View, StyleSheet } from 'react-native';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import Selector from 'design-system/Selector';
import { AppView } from 'design-system/AppView';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { TAB_SCREEN_HEADER_HEIGHT } from 'navigators/styles';
import { logQuest } from 'features/quests/actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import TouchableOpacity from 'utils/packages/TouchableOpacity';
import Text from 'design-system/Text';
import { getSubscriptions } from '../actions';
import Links from '../../../utils/links';
import Amplitude from '../../../utils/amplitude';
import { selectHasDuplicateGroups, selectIncludeIncomeSubscriptions, selectOrderedActiveSubscriptions, } from '../selectors';
import SubscriptionSection from '../components/SubscriptionSection';
import PossibleDuplicateBanner from '../components/PossibleDuplicateBanner';
import { isWeb } from '../../../constants';
import useOpenSubscription from '../hooks/useOpenSubscription';
import RecurringTabHeader from '../components/RecurringTabHeader';
const openInfo = () => Links.openRecurringPayments();
const SubscriptionsScreen = withAnimatedNavigationHeader(({ navigation, onScroll, route }) => {
    const transactionData = route.params && 'data' in route.params ? route.params.data : undefined;
    const transactionId = transactionData?.id;
    const isCreditTransaction = (transactionData?.nativeAmount?.amount ??
        transactionData?.amount ??
        0) > 0;
    const dispatch = useAppDispatch();
    const downloaded = useAppSelector((store) => store.subscriptions.subscriptions.downloaded);
    const isFetching = useAppSelector((store) => store.subscriptions.isFetchingSubscriptions);
    const activeSubscriptions = useAppSelector(selectOrderedActiveSubscriptions);
    const includeIncome = useAppSelector(selectIncludeIncomeSubscriptions);
    const hasDuplicates = useAppSelector(selectHasDuplicateGroups);
    // The attach-transaction flow has no switch: the transaction's
    // direction decides which recurring payments are offered
    // (credits → income, debits → spending).
    const showIncomeSwitch = includeIncome && !transactionId;
    const defaultShowIncome = activeSubscriptions.some((subscription) => subscription.isIncome) &&
        !activeSubscriptions.some((subscription) => !subscription.isIncome);
    const [showIncome, setShowIncome] = useState(defaultShowIncome);
    const showingIncomeRows = transactionId
        ? isCreditTransaction
        : showIncome;
    const visibleSubscriptions = useMemo(() => {
        if (!includeIncome)
            return activeSubscriptions;
        return activeSubscriptions.filter((subscription) => showingIncomeRows ? subscription.isIncome : !subscription.isIncome);
    }, [activeSubscriptions, includeIncome, showingIncomeRows]);
    const onSelectedSection = useCallback((index) => {
        setShowIncome(index === 1);
    }, []);
    const showDuplicateBanner = hasDuplicates && !showingIncomeRows && !transactionId;
    const listHeader = useMemo(() => showIncomeSwitch || showDuplicateBanner ? (<>
              {showIncomeSwitch ? (<View style={styles.selectorContainer}>
                  <Selector Text-14 sections={['Spending', 'Income']} onSelected={onSelectedSection} defaultIndex={showIncome ? 1 : 0}/>
                </View>) : null}
              {showDuplicateBanner ? (<PossibleDuplicateBanner style={styles.duplicateBanner}/>) : null}
            </>) : undefined, 
    // defaultIndex is only read when the Selector first mounts, so
    // showIncome is deliberately not a dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showIncomeSwitch, showDuplicateBanner, onSelectedSection]);
    const openSearch = useCallback(() => {
        navigation.navigateDeprecated('SubscriptionsSearch', {
            income: showingIncomeRows,
            empty: {
                title: 'Add a recurring payment',
                subtitle: 'Search through your transactions and select a recurring payment.\nIt works like magic!',
            },
        });
    }, [showingIncomeRows]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: transactionId ? '' : 'Recurring payments',
            headerRight: transactionId
                ? undefined
                : () => (<TouchableOpacity onCard hoverOnDark onPress={openSearch} style={styles.headerRightBtn}>
                  <Text Text-16>Add</Text>
                </TouchableOpacity>),
        });
    }, [openSearch, transactionId]);
    useEffect(() => {
        dispatch(getSubscriptions(includeIncome));
    }, [includeIncome]);
    useEffect(() => {
        dispatch(logQuest('SUBSCRIPTIONS_OPENED'));
        return undefined;
    }, []);
    const onOpenCompare = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
        Amplitude.logEvent('Subscriptions.openCompare', {
            type: routeWithParams.name,
        });
    }, []);
    const openSubscription = useOpenSubscription();
    const fetching = isFetching && !downloaded;
    const isInTab = route.name === 'RecurringTab';
    return (<AppView withFrame={false} isInTab={isInTab}>
          <View style={styles.container}>
            <StatusBar barStyle="dark-content" animated/>
            <SubscriptionSection openInfo={openInfo} onScroll={onScroll} fetching={fetching} openSearch={openSearch} data={visibleSubscriptions} onOpenCompare={onOpenCompare} onOpenSubscription={openSubscription} transactionId={transactionId} isInTab={isInTab} header={listHeader} income={includeIncome ? showingIncomeRows : undefined} emptySubTitle={includeIncome && showingIncomeRows
            ? 'You don’t have any upcoming income'
            : undefined}/>
          </View>
        </AppView>);
}, ({ colors, route }) => ({
    title: 'Recurring payments',
    hasCustomTitle: !isWeb,
    ...(route.name === 'RecurringTab' &&
        !isWeb && {
        headerHeight: TAB_SCREEN_HEADER_HEIGHT,
        renderContent: () => <RecurringTabHeader />,
    }),
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    selectorContainer: {
        marginHorizontal: rem(16),
        marginTop: rem(8),
        marginBottom: rem(16),
    },
    duplicateBanner: {
        marginBottom: rem(16),
    },
    headerRightBtn: {
        ...(isWeb && {
            marginRight: rem(16),
            paddingVertical: rem(8),
            paddingHorizontal: rem(16),
        }),
    },
});
export default SubscriptionsScreen;
